/**
 * LCDEligibilityOverlay - LCD Eligibility Checker Overlay
 * Evaluates hospice eligibility based on CMS LCD criteria
 */

import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  ClipboardCheck, AlertTriangle, CheckCircle, XCircle, Search,
  FileText, Heart, Brain, Wind, Droplet, Activity
} from "lucide-react";
import api from "@/lib/api";

const LCD_CRITERIA = {
  cancer: {
    name: "Cancer",
    icon: Activity,
    criteria: [
      { id: "metastatic", label: "Metastatic disease or recurrent after treatment", required: true },
      { id: "decline_treatment", label: "Patient has declined further curative treatment", required: true },
      { id: "functional_decline", label: "Significant functional decline (PPS ≤ 50%)", required: false },
      { id: "weight_loss", label: "Unintentional weight loss > 10% in 6 months", required: false },
      { id: "albumin", label: "Serum albumin < 2.5 g/dL", required: false }
    ]
  },
  heart_disease: {
    name: "Heart Disease",
    icon: Heart,
    criteria: [
      { id: "nyha_class", label: "NYHA Class III or IV despite optimal treatment", required: true },
      { id: "ef", label: "Ejection fraction ≤ 20%", required: false },
      { id: "symptoms_rest", label: "Symptoms at rest or with minimal activity", required: true },
      { id: "hospitalization", label: "Hospitalization for CHF in past 6 months", required: false },
      { id: "arrhythmia", label: "Documented history of resistant arrhythmias", required: false }
    ]
  },
  dementia: {
    name: "Dementia",
    icon: Brain,
    criteria: [
      { id: "fast_7c", label: "FAST Stage 7c or beyond", required: true },
      { id: "dependency", label: "Unable to ambulate, dress, or bathe without assistance", required: true },
      { id: "incontinence", label: "Urinary and fecal incontinence", required: false },
      { id: "speech", label: "Limited speech (≤6 intelligible words/day)", required: false },
      { id: "comorbidity", label: "Medical comorbidities in past 12 months (pneumonia, UTI, sepsis)", required: false }
    ]
  },
  copd: {
    name: "COPD/Pulmonary Disease",
    icon: Wind,
    criteria: [
      { id: "dyspnea", label: "Disabling dyspnea at rest", required: true },
      { id: "fev1", label: "FEV1 < 30% predicted (after bronchodilators)", required: false },
      { id: "oxygen", label: "Oxygen dependent", required: false },
      { id: "cor_pulmonale", label: "Cor pulmonale or right heart failure", required: false },
      { id: "weight_loss", label: "Progressive unintentional weight loss > 10%", required: false }
    ]
  },
  renal_disease: {
    name: "Renal Disease",
    icon: Droplet,
    criteria: [
      { id: "gfr", label: "GFR < 10 ml/min (or creatinine > 8.0)", required: true },
      { id: "dialysis_declined", label: "Patient not seeking or discontinued dialysis", required: true },
      { id: "uremia", label: "Signs of uremia (confusion, nausea, intractable pruritus)", required: false },
      { id: "oliguria", label: "Oliguria (< 400ml/24hrs)", required: false }
    ]
  }
};

export default function LCDEligibilityOverlay({ isOpen, onClose, patientId, patientName }) {
  const [selectedCondition, setSelectedCondition] = useState("");
  const [checkedCriteria, setCheckedCriteria] = useState({});
  const [kps, setKps] = useState("");
  const [pps, setPps] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Reset state when condition changes
  const resetForm = () => {
    setCheckedCriteria({});
    setResult(null);
  };

  // Handle condition change - reset criteria and result
  const handleConditionChange = (condition) => {
    setSelectedCondition(condition);
    resetForm();
  };

  const handleCriteriaChange = (criteriaId, checked) => {
    setCheckedCriteria(prev => ({
      ...prev,
      [criteriaId]: checked
    }));
  };

  const calculateEligibility = () => {
    if (!selectedCondition) {
      toast.error("Please select a primary diagnosis");
      return;
    }

    const criteria = LCD_CRITERIA[selectedCondition].criteria;
    const requiredCriteria = criteria.filter(c => c.required);
    const supportingCriteria = criteria.filter(c => !c.required);
    
    const requiredMet = requiredCriteria.filter(c => checkedCriteria[c.id]).length;
    const supportingMet = supportingCriteria.filter(c => checkedCriteria[c.id]).length;
    
    const requiredScore = requiredCriteria.length > 0 ? (requiredMet / requiredCriteria.length) * 100 : 100;
    const supportingScore = supportingCriteria.length > 0 ? (supportingMet / supportingCriteria.length) * 100 : 0;
    
    // Additional scoring factors
    let bonusScore = 0;
    if (parseInt(pps) <= 50) bonusScore += 15;
    if (parseInt(pps) <= 40) bonusScore += 10;
    if (parseInt(kps) <= 50) bonusScore += 10;

    const totalScore = Math.min(100, (requiredScore * 0.7) + (supportingScore * 0.2) + bonusScore);
    
    const eligible = requiredMet === requiredCriteria.length && totalScore >= 70;
    
    setResult({
      eligible,
      score: Math.round(totalScore),
      requiredMet,
      requiredTotal: requiredCriteria.length,
      supportingMet,
      supportingTotal: supportingCriteria.length,
      condition: LCD_CRITERIA[selectedCondition].name,
      recommendation: eligible 
        ? "Patient meets LCD criteria for hospice eligibility" 
        : requiredMet < requiredCriteria.length 
          ? "Required criteria not fully met - review documentation"
          : "Consider additional supporting documentation"
    });

    // Save to backend
    saveEligibilityCheck({
      eligible,
      score: totalScore,
      condition: selectedCondition,
      checkedCriteria,
      kps,
      pps,
      notes
    });
  };

  const saveEligibilityCheck = async (data) => {
    try {
      await api.post("/lcd-eligibility/check", {
        patient_id: patientId,
        ...data,
        checked_at: new Date().toISOString()
      });
      toast.success("Eligibility check saved to patient chart");
    } catch (err) {
      // Silent fail - demo mode
    }
  };

  const currentCriteria = selectedCondition ? LCD_CRITERIA[selectedCondition] : null;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[800px] max-w-[95vw] bg-slate-900 border-slate-700 text-white overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-emerald-400" />
            LCD Eligibility Checker
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            Evaluate hospice eligibility using CMS Local Coverage Determination criteria
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Patient Info */}
          {patientName && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-slate-300">
                  <FileText className="w-4 h-4" />
                  <span>Evaluating: <strong className="text-white">{patientName}</strong></span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Condition Selection */}
          <div className="space-y-2">
            <Label className="text-slate-300">Primary Terminal Diagnosis</Label>
            <Select value={selectedCondition} onValueChange={handleConditionChange}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                <SelectValue placeholder="Select primary diagnosis..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                {Object.entries(LCD_CRITERIA).map(([key, value]) => (
                  <SelectItem key={key} value={key} className="text-white hover:bg-slate-700">
                    {value.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Performance Scores */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300">PPS Score (%)</Label>
              <Input
                type="number"
                value={pps}
                onChange={(e) => setPps(e.target.value)}
                placeholder="e.g., 40"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">KPS Score (%)</Label>
              <Input
                type="number"
                value={kps}
                onChange={(e) => setKps(e.target.value)}
                placeholder="e.g., 50"
                className="bg-slate-800 border-slate-600 text-white"
              />
            </div>
          </div>

          {/* LCD Criteria Checklist */}
          {currentCriteria && (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <currentCriteria.icon className="w-5 h-5 text-teal-400" />
                  {currentCriteria.name} - LCD Criteria
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <p className="text-sm text-amber-400 font-medium">Required Criteria:</p>
                  {currentCriteria.criteria.filter(c => c.required).map(criteria => (
                    <div key={criteria.id} className="flex items-start gap-3">
                      <Checkbox
                        id={criteria.id}
                        checked={checkedCriteria[criteria.id] || false}
                        onCheckedChange={(checked) => handleCriteriaChange(criteria.id, checked)}
                        className="mt-0.5 border-slate-500 data-[state=checked]:bg-emerald-500"
                      />
                      <Label htmlFor={criteria.id} className="text-slate-300 cursor-pointer">
                        {criteria.label}
                      </Label>
                    </div>
                  ))}
                </div>

                <div className="space-y-3 pt-4 border-t border-slate-700">
                  <p className="text-sm text-slate-400 font-medium">Supporting Documentation:</p>
                  {currentCriteria.criteria.filter(c => !c.required).map(criteria => (
                    <div key={criteria.id} className="flex items-start gap-3">
                      <Checkbox
                        id={criteria.id}
                        checked={checkedCriteria[criteria.id] || false}
                        onCheckedChange={(checked) => handleCriteriaChange(criteria.id, checked)}
                        className="mt-0.5 border-slate-500 data-[state=checked]:bg-teal-500"
                      />
                      <Label htmlFor={criteria.id} className="text-slate-400 cursor-pointer">
                        {criteria.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Clinical Notes */}
          <div className="space-y-2">
            <Label className="text-slate-300">Additional Clinical Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Document additional clinical findings supporting eligibility..."
              className="bg-slate-800 border-slate-600 text-white min-h-[100px]"
            />
          </div>

          {/* Evaluate Button */}
          <Button 
            onClick={calculateEligibility}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            disabled={!selectedCondition}
          >
            <Search className="w-4 h-4 mr-2" />
            Evaluate Eligibility
          </Button>

          {/* Results */}
          {result && (
            <Card className={`border-2 ${result.eligible ? 'bg-emerald-900/30 border-emerald-500' : 'bg-amber-900/30 border-amber-500'}`}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  {result.eligible ? (
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  ) : (
                    <AlertTriangle className="w-8 h-8 text-amber-400" />
                  )}
                  <div>
                    <p className={`font-bold text-lg ${result.eligible ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {result.eligible ? 'ELIGIBLE' : 'NEEDS REVIEW'}
                    </p>
                    <p className="text-sm text-slate-400">{result.condition}</p>
                  </div>
                  <Badge variant="outline" className="ml-auto text-lg px-3 py-1">
                    {result.score}%
                  </Badge>
                </div>

                <Progress value={result.score} className="h-2 mb-4" />

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-400">Required Criteria</p>
                    <p className="text-white font-medium">{result.requiredMet} of {result.requiredTotal} met</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Supporting Documentation</p>
                    <p className="text-white font-medium">{result.supportingMet} of {result.supportingTotal} documented</p>
                  </div>
                </div>

                <p className="mt-4 text-sm text-slate-300 bg-slate-800/50 p-3 rounded">
                  {result.recommendation}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
